import { Eye, FileText, Kanban, List, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import {
  CONTRATO_STATUS_LABELS,
  normalizeAtivoTipo,
  type AtivoTipo,
  type ContractPipelineCard,
  type ContratoDto,
  type ContratoPipelineFase,
  type ContratoStatus,
  type MaquinarioDto,
} from "@/services/locacoes";
import { formatDate } from "@/utils/format";
import { cn } from "@/utils/cn";
import { formatContratoValor, locacoesPillToggleClass, locacoesTabContentClass, locacoesTableHeadClass, locacoesTableWrapClass } from "./locacoesUtils";
import { ContratoKanbanBoard } from "./ContratoKanbanBoard";
import { ContratoTipoTabs } from "./ContratoTipoTabs";
import { LocacoesStatCard } from "./LocacoesStatCard";
import { LocacoesSubToolbar } from "./LocacoesSubToolbar";

type ViewMode = "lista" | "pipeline";

interface Props {
  contratos: ContratoDto[];
  pipelineCards: ContractPipelineCard[];
  maquinarios: MaquinarioDto[];
  busy?: boolean;
  onNovo?: (tipo: AtivoTipo) => void;
  onEdit?: (c: ContratoDto) => void;
  onDelete?: (c: ContratoDto) => void;
  onView: (c: ContratoDto) => void;
  onPipelineMove?: (contratoId: string, fase: ContratoPipelineFase) => Promise<void>;
}

function maquinasLabel(ids: string[], maquinarios: MaquinarioDto[]): string {
  if (ids.length === 0) return "—";
  const names = ids
    .map((id) => maquinarios.find((m) => m.id === id)?.nome)
    .filter(Boolean) as string[];
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

export function TabContratos({
  contratos,
  pipelineCards,
  maquinarios,
  busy,
  onNovo,
  onEdit,
  onDelete,
  onView,
  onPipelineMove,
}: Props) {
  const [view, setView] = useState<ViewMode>(pipelineCards.length > 0 ? "pipeline" : "lista");
  const [busca, setBusca] = useState("");
  const [statusFilter, setStatusFilter] = useState<ContratoStatus | "">("");
  const [tipoTab, setTipoTab] = useState<AtivoTipo>("maquinario");

  const contratosPorTipo = useMemo(
    () => contratos.filter((c) => normalizeAtivoTipo(c.tipoContrato) === tipoTab),
    [contratos, tipoTab],
  );

  const pipelinePorTipo = useMemo(
    () => pipelineCards.filter((p) => contratosPorTipo.some((c) => c.id === p.id)),
    [pipelineCards, contratosPorTipo],
  );

  const kpiStats = useMemo(
    () => ({
      total: contratosPorTipo.length,
      ativos: contratosPorTipo.filter((c) => c.status === "ativo").length,
      esperando: contratosPorTipo.filter((c) => c.status === "esperando_ativacao").length,
      pipeline: pipelinePorTipo.length,
    }),
    [contratosPorTipo, pipelinePorTipo],
  );

  const filtered = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return contratosPorTipo.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (!q) return true;
      return (
        c.clienteSiengeNome.toLowerCase().includes(q) ||
        c.clienteSiengeDocument.toLowerCase().includes(q) ||
        c.modeloContrato.toLowerCase().includes(q) ||
        c.infoBasicos.toLowerCase().includes(q)
      );
    });
  }, [contratosPorTipo, busca, statusFilter]);

  const filteredPipeline = useMemo(() => {
    const byId = new Map(filtered.map((c) => [c.id, c]));
    return pipelineCards
      .filter((p) => byId.has(p.id))
      .map((p) => ({
        ...p,
        infoBasicos: p.infoBasicos?.trim() || byId.get(p.id)?.infoBasicos || "",
      }));
  }, [pipelineCards, filtered]);

  const resolveKanbanCard = (card: ContractPipelineCard) => contratos.find((x) => x.id === card.id);

  const handleKanbanView = (card: ContractPipelineCard) => {
    const c = resolveKanbanCard(card);
    if (c) onView(c);
  };

  const handleKanbanEdit = (card: ContractPipelineCard) => {
    const c = resolveKanbanCard(card);
    if (c && onEdit) onEdit(c);
  };

  const handleKanbanDelete = (card: ContractPipelineCard) => {
    const c = resolveKanbanCard(card);
    if (c && onDelete) onDelete(c);
  };

  return (
    <div className={locacoesTabContentClass}>
      <ContratoTipoTabs value={tipoTab} onChange={setTipoTab} className="max-w-md" />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <LocacoesStatCard
          title="Total de contratos"
          value={kpiStats.total}
          icon={<FileText className="h-5 w-5" />}
          accentClass="border-l-4 border-l-blue-400"
        />
        <LocacoesStatCard
          title="Ativos"
          value={kpiStats.ativos}
          icon={<FileText className="h-5 w-5" />}
          accentClass="border-l-4 border-l-emerald-400"
        />
        <LocacoesStatCard
          title="Aguardando ativação"
          value={kpiStats.esperando}
          icon={<FileText className="h-5 w-5" />}
          accentClass="border-l-4 border-l-amber-400"
        />
        <LocacoesStatCard
          title="No pipeline"
          value={kpiStats.pipeline}
          icon={<Kanban className="h-5 w-5" />}
          accentClass="border-l-4 border-l-purple-400"
        />
      </div>

      <LocacoesSubToolbar
        end={
          onNovo ? (
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => onNovo(tipoTab)}>
              Novo contrato
            </Button>
          ) : undefined
        }
      >
        <div className="flex min-w-0 flex-wrap items-end gap-3">
          <div className="relative min-w-0 flex-1 basis-[200px] max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar cliente, documento ou modelo…"
              className="pl-9"
            />
          </div>
          <Select
            label=""
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ContratoStatus | "")}
            options={[
              { value: "", label: "Todos os status" },
              ...Object.entries(CONTRATO_STATUS_LABELS).map(([value, label]) => ({ value, label })),
            ]}
            className="min-w-[140px]"
          />
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {pipelinePorTipo.length > 0 && (
              <button
                type="button"
                onClick={() => setView("pipeline")}
                className={locacoesPillToggleClass(view === "pipeline")}
              >
                <Kanban className="h-3.5 w-3.5" />
                Pipeline
              </button>
            )}
            <button type="button" onClick={() => setView("lista")} className={locacoesPillToggleClass(view === "lista")}>
              <List className="h-3.5 w-3.5" />
              Lista
            </button>
          </div>
        </div>
      </LocacoesSubToolbar>

      {view === "pipeline" && onPipelineMove ? (
        <ContratoKanbanBoard
          cards={filteredPipeline}
          busy={busy}
          onMove={onPipelineMove}
          onView={handleKanbanView}
          onEdit={onEdit ? handleKanbanEdit : undefined}
          onDelete={onDelete ? handleKanbanDelete : undefined}
        />
      ) : view === "pipeline" ? (
        <p className="rounded-xl border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500 shadow-card">
          Selecione um contrato na lista para registrar horímetros e fotos.
        </p>
      ) : (
        <div className={locacoesTableWrapClass}>
          <table className="min-w-full text-sm">
            <thead className={locacoesTableHeadClass}>
              <tr>
                <th className="px-4 py-3">Modelo de contrato</th>
                <th className="px-4 py-3">Cliente (Sienge)</th>
                <th className="px-4 py-3">Valor</th>
                <th className="px-4 py-3">{tipoTab === "equipamento" ? "Equipamentos" : "Máquinas"}</th>
                <th className="px-4 py-3">Info básicas</th>
                <th className="px-4 py-3">Operadores</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                    <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                    {contratos.length === 0
                      ? 'Nenhum contrato cadastrado. Clique em "Novo contrato" para começar.'
                      : "Nenhum contrato corresponde aos filtros."}
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3 font-medium text-slate-800">{c.modeloContrato}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{c.clienteSiengeNome || "—"}</p>
                      {c.clienteSiengeDocument && (
                        <p className="text-xs text-slate-500">{c.clienteSiengeDocument}</p>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 tabular-nums">{formatContratoValor(c)}</td>
                    <td className="max-w-[10rem] px-4 py-3 text-slate-600" title={maquinasLabel(c.maquinaIds, maquinarios)}>
                      {maquinasLabel(c.maquinaIds, maquinarios)}
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-slate-500" title={c.infoBasicos}>
                      {c.infoBasicos || "—"}
                    </td>
                    <td className="px-4 py-3 tabular-nums">{c.operadores.length}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2 py-0.5 text-xs font-semibold",
                          c.status === "ativo" && "bg-emerald-50 text-emerald-700",
                          c.status === "esperando_ativacao" && "bg-amber-50 text-amber-800",
                          c.status === "inativo" && "bg-slate-100 text-slate-600",
                          c.status === "encerrado" && "bg-slate-100 text-slate-600",
                          c.status === "cancelado" && "bg-red-50 text-red-700",
                        )}
                      >
                        {CONTRATO_STATUS_LABELS[c.status]}
                      </span>
                      <p className="mt-0.5 text-[10px] text-slate-400">Início {formatDate(c.dataInicio)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={() => onView(c)}
                          leftIcon={<Eye className="h-3.5 w-3.5" />}
                          title="Ver operação do contrato"
                        >
                          Ver
                        </Button>
                        {onEdit && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(c)}
                            leftIcon={<Pencil className="h-3.5 w-3.5" />}
                          >
                            Editar
                          </Button>
                        )}
                        {onDelete && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(c)}
                            leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                          >
                            Excluir
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
